import type {
  CurrentAssetIntelligenceAsset,
  CurrentAssetIntelligenceBrowserSafeProjectionProps,
  CurrentAssetIntelligenceOpportunity,
  CurrentAssetIntelligenceRegistryAsset,
} from "./browserSafeReadModel";
import type { ExistingSourceRunCandidate } from "./existingSourceMapping";

export type ExistingSourceReadPayload = ExistingSourceRunCandidate & {
  sourceRowCounts?: Record<string, number>;
  sourceWarnings?: string[];
  assetRegistry?: CurrentAssetIntelligenceRegistryAsset[];
};

const text = (value: unknown, fallback: string): string => {
  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || fallback;
  }
  if (Array.isArray(value)) {
    const normalized = value
      .filter((item) => typeof item === "string" || typeof item === "number")
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(", ");
    return normalized || fallback;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of ["label", "name", "title", "display_name", "assetDisplayName", "asset_name", "asset_type", "journeyStage", "sourceTable", "url"]) {
      if (record[key] !== undefined) {
        const normalized = text(record[key], "");
        if (normalized) return normalized;
      }
    }
  }
  return fallback;
};

const priority = (value: unknown): "P0" | "P1" | "P2" => {
  const normalized = String(value ?? "").toUpperCase();
  return normalized === "P0" || normalized === "P1" ? normalized : "P2";
};

function journeyStage(category: string, fallback: string): string {
  const value = category.toLowerCase();
  if (value.includes("local") || value.includes("location")) return "Local discovery";
  if (value.includes("brand") || value.includes("entity")) return "Entity clarity";
  if (value.includes("competitive") || value.includes("competitor")) return "Competitive consideration";
  if (value.includes("purchase") || value.includes("commercial")) return "Commercial intent";
  return fallback;
}

function assetKind(category: string): CurrentAssetIntelligenceAsset["kind"] {
  const value = category.toLowerCase();
  if (value.includes("local") || value.includes("location")) return "Local / location";
  if (value.includes("product") || value.includes("solution")) return "Product / solution";
  if (value.includes("service") || value.includes("category")) return "Category / service";
  if (value.includes("pdf") || value.includes("resource")) return "PDF / resource";
  return "Homepage";
}

function mapPromptResultAsset(row: Record<string, unknown>, index: number, completedAt: string): CurrentAssetIntelligenceAsset {
  const category = text(row.category, "journey");
  const displayName = `${category} journey asset ${index + 1}`;
  const stage = journeyStage(category, "Discovery and evaluation");
  const status = row.status === "completed" ? "review" : "blocked";
  return {
    assetId: text(row.id, `prompt-result-${index + 1}`),
    kind: assetKind(category),
    title: displayName,
    assetDisplayName: displayName,
    assetSourceType: "prompt_result_journey_asset",
    journeyStage: stage,
    promptCategory: category,
    sourceTable: "prompt_results",
    path: "",
    hasCanonicalUrl: false,
    canonicalUrl: null,
    lastReviewed: text(row.completed_at || row.created_at, completedAt),
    statuses: {
      assetCoverage: { status, value: row.status === "completed" ? "Prompt covered" : "Review", detail: "Derived from a completed prompt result; no canonical URL was present." },
      entityClarity: { status: "review", value: "Needs mapping", detail: "Prompt-result journey asset; entity mapping is not canonical." },
      aiExtractability: { status: "blocked", value: "Not executed", detail: "Existing-source mapping does not call AI." },
      competitiveCompleteness: { status: "review", value: "Partial", detail: "Competitive completeness requires a dedicated asset inventory or confirmed view." },
      freshnessTrust: { status: "review", value: "Run scoped", detail: "Bound to the verified Toren run." },
    },
  };
}

function mapBrief(row: Record<string, unknown>): CurrentAssetIntelligenceOpportunity {
  const id = text(row.id, "brief-unknown");
  const title = text(row.title, text(row.action_type, "Opportunity action brief"));
  const action = text(row.recommended_action, title);
  const category = text(row.product_area, "Opportunity operations");
  return {
    opportunityId: `brief:${id}`,
    priority: priority(row.priority),
    opportunityType: `Action brief · ${category}`,
    affectedJourneyStage: journeyStage(category, "Internal opportunity review"),
    finding: title,
    affectedAsset: text(row.affected_asset || row.asset_name || row.asset_title || row.source_context, "Competitive opportunity"),
    recommendedInternalAction: action,
    recommendedAction: action,
    estimatedImpact: "Internal review signal",
    implementationEffort: "Review",
    status: row.is_active === false ? "blocked" : "review",
    safetyStatus: "Internal only",
    safetyNote: "Static existing-source mapping; no attribution, customer output, or write action.",
    sourceTable: "opportunity_action_briefs",
    sourceRecordId: id,
    entityName: text(row.entity_name || row.brand_name, ""),
    competitorNames: text(row.competitors || row.competitor || row.competitor_names, ""),
    promptCategory: text(row.prompt_category || row.category || row.product_area, ""),
    contentBrief: text(row.content_brief, ""),
    suggestedStructure: text(row.suggested_structure, ""),
    sourceContext: text(row.source_context, ""),
  };
}

function mapEntityRecommendation(row: Record<string, unknown>): CurrentAssetIntelligenceOpportunity {
  const id = text(row.id, "entity-recommendation-unknown");
  const title = text(row.title, text(row.entity_name, "Entity recommendation"));
  const action = text(row.recommended_action, text(row.summary, title));
  const category = text(row.entity_area, text(row.recommendation_type, "Entity clarity"));
  return {
    opportunityId: `entity:${id}`,
    priority: priority(row.priority),
    opportunityType: `Entity recommendation · ${category}`,
    affectedJourneyStage: "Entity clarity",
    finding: title,
    affectedAsset: text(row.entity_name || row.asset_name || row.source_context, "Entity profile"),
    recommendedInternalAction: action,
    recommendedAction: action,
    estimatedImpact: "Improve internal entity clarity",
    implementationEffort: "Review",
    status: row.status === "ready" ? "ready" : "review",
    safetyStatus: "Internal only",
    safetyNote: "Static existing-source mapping; no entity record or customer output is created.",
    sourceTable: "entity_builder_recommendations",
    sourceRecordId: id,
    entityName: text(row.entity_name || row.brand_name, ""),
    competitorNames: text(row.competitors || row.competitor || row.competitor_names, ""),
    promptCategory: text(row.prompt_category || row.category || row.entity_area, ""),
    contentBrief: text(row.content_brief, ""),
    suggestedStructure: text(row.suggested_structure, ""),
    sourceContext: text(row.source_context, ""),
  };
}

export function mapExistingSourceReadToBrowserSafeReadModel(payload: ExistingSourceReadPayload): CurrentAssetIntelligenceBrowserSafeProjectionProps {
  const account = payload.account || {};
  const run = payload.run || {};
  const accountId = text(account.account_id, "unscoped-account");
  const runId = text(run.run_id, "unscoped-run");
  const domain = text(account.website_domain, "domain-not-supplied");
  const completedAt = text(run.completed_at || run.started_at, "Not timestamped");
  const assets = (payload.promptResults || []).map((row, index) => mapPromptResultAsset(row, index, completedAt));
  const opportunityQueue = [
    ...(payload.opportunityActionBriefs || []).map(mapBrief),
    ...(payload.entityBuilderRecommendations || []).map(mapEntityRecommendation),
  ];
  const sourceWarnings = Array.from(new Set(payload.sourceWarnings || []));
  const partial = sourceWarnings.length > 0 || assets.length === 0;
  return {
    visibility: "internal_admin_browser_safe_route_fixture_props",
    routePath: "/admin/current-asset-intelligence",
    componentName: "CurrentAssetIntelligenceBrowserSafe",
    projectionStatus: "validated_internal_ui_projection",
    scenario: "torenVerifiedExistingSource",
    sourceMode: payload.sourceMode,
    runDate: completedAt === "Not timestamped" ? undefined : completedAt,
    profileLabel: "Verified Toren existing-source snapshot",
    scenarioLabel: partial ? "Existing-source verified snapshot with optional-source warnings" : "Existing-source verified snapshot",
    scenarioDetail: "Static snapshot mapped locally; it is not a browser read and does not enable the hidden route.",
    projectionSections: { statusHeader: true, safetyCards: true, routeDiagnostics: true, browserSafetyLocks: true, customerExposureLocks: true, counterPanel: true, blockedActions: true, nextSteps: true, rollbackPanel: true, debugMetadata: true },
    accountRunContext: { accountLabel: `${text(account.business_name || account.display_name, "Toren account")} · ${domain}`, accountId, runId, source: "Static internal fixture", dataMode: "Demo only" },
    readoutSummary: { headline: "Existing-source CAI compatibility readout", detail: `Mapped from a verified ${text(run.status, "completed")} Toren run snapshot.`, status: "internal-only", scope: `${accountId} · ${runId}` },
    assetCoverage: { label: "Asset coverage", value: `${assets.length} prompt-result journey assets`, detail: "Assets are journey-level prompt-result projections until a true URL inventory exists.", status: assets.length ? "review" : "blocked" },
    entityClarity: { label: "Entity clarity", value: `${(payload.entityBuilderRecommendations || []).length} recommendations`, detail: "Mapped from entity_builder_recommendations.", status: "review" },
    aiExtractability: { label: "AI extractability", value: "Not executed", detail: "No AI call is part of this adapter.", status: "blocked" },
    competitiveCompleteness: { label: "Competitive completeness", value: "Optional sources", detail: sourceWarnings.length ? sourceWarnings.join("; ") : "Mapped from available existing sources.", status: sourceWarnings.length ? "review" : "internal-only" },
    recommendedNextSteps: ["Review prompt-result journey labels before treating them as canonical assets.", "Review the ai_opportunity_engine_v2 timeout before making that view a v1 dependency.", "Keep the hidden route on its default fixture until a separate server-read approval enables a controlled path."],
    previewSummary: { totalAssetsReviewed: assets.length, assetsReadyForInternalReview: assets.filter((asset) => asset.statuses.assetCoverage.status !== "blocked").length, blockedProductionActions: 0, customerFacingOutputs: 0, browserSupabaseReads: 0 },
    assetInventory: assets,
    assetRegistry: Array.isArray(payload.assetRegistry) ? payload.assetRegistry.slice(0, 25) : [],
    opportunityQueue,
    sourceWarnings,
    sourceRowCounts: payload.sourceRowCounts || {},
    safetyLocks: { customerOutputDisabled: true, browserSupabaseReads: 0, browserSupabaseWrites: 0, customerFacing: false, customerRouteMounted: false, customerAccessGranted: false, customerReadModelExposed: false, featureFlagWritten: false, adminUiRendered: false, aiCalls: 0, liveNetworkActions: 0 },
  };
}
